import _ from 'underscore';
import $ from 'jquery';
import React from 'react';
import OptionPopupView from "views/common/OptionPopupView";
import { createRoot } from "react-dom/client";
import { ExportContent } from '../../components/audioEditor/ExportContent';
import RSVP from "rsvp";

const ExportPopupView = OptionPopupView.extend({

    pageName: "Export Selection",

    // ========================================
    // INITIALIZATION
    // ========================================

    initialize: function(options) {
        // Store configuration options
        this._hasVideo = options.hasVideo || false;
        this._isSelection = options.isSelection || false;
        this._isSequencerAlreadyInstantiated = options.isSequencerAlreadyInstantiated || false;
        this._isVideoReady = options.isVideoReady !== undefined ? options.isVideoReady : true;
        this._isVideoBeingRendered = options.isVideoBeingRendered || false;
        this._onExport = options.onExport;
        this._i18nModule = options.i18nModule;
        // Progress bar is now managed by the parent controller, not directly by this view
        
        // Initialize state
        this._isExporting = false;
        this._exportWidgetRoot = null;

        // Create container structure
        this._createContainers();
        
        // Initialize parent popup view
        this._initializeParentView();
    },

    _createContainers: function() {
        // Create main container that will hold both export and progress content
        this.mainContainer$el = $('<div class="export-popup-main-container"></div>');
        this.mainContainer$el.css({
            "width": "calc(100% - 40px)",
            "max-width": "500px"
        });

        // Create container for export content
        this.exportContainer$el = $('<div class="export-popup-content"></div>');

        // Create progress bar container (initially hidden)
        this.progressContainer$el = $('<div class="export-progress-content" style="display: none; text-align: center;"></div>');
        
        // Append both containers to main container
        this.mainContainer$el.append(this.exportContainer$el);
        this.mainContainer$el.append(this.progressContainer$el);
    },

    _initializeParentView: function() {
        const contentView = {
            $el: this.mainContainer$el
        };

        const viewOptions = {
            contentView: contentView,
            contentPosition: "text-not-white",
            addContentAfterEnhancement: true,
            displayTitle: false,
            titleToDisplay: "",
            id: "ExportPopup"
        };

        OptionPopupView.prototype.initialize.call(this, viewOptions);
    },

    // ========================================
    // LIFECYCLE METHODS
    // ========================================

    afterRendering: function() {
        // Called after the popup is rendered
    },

    onAfterPageShow: function() {
        // Progress bar view will be attached by the parent controller
        
        // Render React component after popup is shown
        this.addExportWidget();
    },

    afterRender: function() {
        OptionPopupView.prototype.afterRender.call(this);
    },

    onPopupClosed: function() {
        this._cleanupReactComponent();
    },

    // ========================================
    // CONTAINER MANAGEMENT
    // ========================================

    /**
     * Attach progress bar view (called by controller)
     */
    attachProgressBarView: function(progressBarView) {
        if (progressBarView && progressBarView.$el) {
            this.progressContainer$el.append(progressBarView.$el);
            this._progressBarView = progressBarView;
        }
    },

    _cleanupReactComponent: function() {
        if (this._exportWidgetRoot) {
            this._exportWidgetRoot.unmount();
            this._exportWidgetRoot = null;
        }
    },


    // ========================================
    // REACT COMPONENT MANAGEMENT
    // ========================================

    addExportWidget: function() {
        // Unmount existing component if any
        this._cleanupReactComponent();

        // Create new React root and render component
        const root = createRoot(this.exportContainer$el.get(0));
        
        root.render(
            React.createElement(ExportContent, {
                hasVideo: this._hasVideo,
                isSelection: this._isSelection,
                isSequencerAlreadyInstantiated: this._isSequencerAlreadyInstantiated,
                isVideoReady: this._isVideoReady,
                isVideoBeingRendered: this._isVideoBeingRendered,
                onExport: this.onExportTriggered.bind(this),
                onCancel: this.onCancelTriggered.bind(this),
                i18nModule: this._i18nModule,
                useEditorTheme: true
                // TODO: Add FPS and resolution override props for user-selectable video parameters
                // detectedFps: <number>, // Auto-detected maximum FPS from video nodes
                // detectedResolution: { width: <number>, height: <number> }, // A-roll resolution
                // onFpsChange: (fps) => {}, // Callback for user FPS override
                // onResolutionChange: (resolution) => {} // Callback for user resolution override
            })
        );
        
        this._exportWidgetRoot = root;
    },

    // ========================================
    // PROGRESS BAR MANAGEMENT
    // ========================================

    showProgressBar: function(message) {
        if (!this._progressBarView) {
            console.warn('No progress bar view attached');
            return RSVP.Promise.resolve();
        }
        
        this._showProgressBarInternal(message);
        return RSVP.Promise.resolve();
    },

    _showProgressBarInternal: function(message) {
        this.exportContainer$el.hide();
        this.progressContainer$el.show();
        
        // Note: Progress bar state (indeterminate, message) is now managed by the controller
        // This view only handles showing/hiding the containers
    },

    hideProgressBar: function() {
        if (this.progressContainer$el) {
            this.progressContainer$el.hide();
        }
        this.exportContainer$el.show();
    },

    updateProgress: function(percentage, message) {
        // Note: Actual progress updates are now handled by the controller
        // This method exists for API compatibility but delegates to controller
        // The controller will call the progress bar controller methods directly
    },

    // ========================================
    // EVENT HANDLERS
    // ========================================

    onExportTriggered: async function(exportOptions) {
        if (!this._onExport) {
            return;
        }

        try {
            // Show progress bar with initial message
            await this.showProgressBar(
                window.getI18n(this._i18nModule, 'PREPARING_EXPORT')
            );
            
            // Small delay to ensure progress bar is visible
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Execute export (progress tracking handled by controller)
            await this._onExport(exportOptions);
            
            // Small delay before closing to show completion
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Close popup on successful export
            this.close();
            
        } catch (error) {
            // Hide progress bar and show form again on error
            this.hideProgressBar();
            console.error('Export failed:', error);
            // The controller already displayed the user-facing error. Keep the
            // rejected export from escaping the React click handler as an
            // unhandled promise rejection.
        }
    },

    onCancelTriggered: function() {
        this.close();
    }
});

export default ExportPopupView;
